import { useEffect, useState } from 'react'
import {
  curatedProjects,
  featuredProjectName,
  type Project,
} from '../data/projects'

type GithubRepository = Pick<
  Project,
  | 'name'
  | 'homepage'
  | 'language'
  | 'stargazers_count'
  | 'forks_count'
  | 'topics'
  | 'updated_at'
>

type ProjectState = {
  projects: Project[]
  isLive: boolean
}

const endpoint = 'https://api.github.com/users/PMZFX/repos?per_page=100&sort=updated'

export function useGithubProjects(): ProjectState {
  const [state, setState] = useState<ProjectState>({
    projects: curatedProjects.filter((project) => project.visibility !== 'hidden'),
    isLive: false,
  })

  useEffect(() => {
    const controller = new AbortController()

    async function loadProjects() {
      try {
        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: { Accept: 'application/vnd.github+json' },
        })

        if (!response.ok) throw new Error(`GitHub returned ${response.status}`)

        const repositories = (await response.json()) as GithubRepository[]
        const repositoriesByName = new Map(repositories.map((repository) => [repository.name, repository]))

        const projects = curatedProjects
          .filter((project) => project.visibility !== 'hidden')
          .map((curated) => {
            const repository = repositoriesByName.get(curated.name)
            if (!repository) return curated

            return {
              ...curated,
              homepage: curated.homepage ?? repository.homepage,
              language: repository.language ?? curated.language,
              stargazers_count: repository.stargazers_count,
              forks_count: repository.forks_count,
              topics: repository.topics?.length ? repository.topics : curated.topics,
              updated_at: repository.updated_at,
              feature: curated.name === featuredProjectName,
            }
          })

        setState({ projects, isLive: true })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setState({ projects: curatedProjects.filter((project) => project.visibility !== 'hidden'), isLive: false })
      }
    }

    loadProjects()
    return () => controller.abort()
  }, [])

  return state
}
